import { Controller, useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { jobsQueryKey, listJobs } from '@/lib/api/jobs';

import { lineItemsTotal, toLineItems } from './helpers';
import { LineItemsField } from './line-items-field';
import { addQuoteSchema, type AddQuoteInput, type QuoteFieldsValues } from './schema';
import type { NewQuoteSubmission } from './types';

interface Props {
  businessId: string;
  // When provided, the job is fixed (opened from a job) and the picker is hidden.
  jobId?: string;
  jobTitle?: string;
  onSave: (submission: NewQuoteSubmission) => void;
  onClose: () => void;
  isSaving: boolean;
}

const DEFAULT_VALUES: AddQuoteInput = {
  jobId: '',
  lineItems: [],
  notes: '',
  manualTotal: false,
  totalAmount: '',
};

export function AddQuoteModal({
  businessId,
  jobId,
  jobTitle,
  onSave,
  onClose,
  isSaving,
}: Props) {
  const { t } = useLanguage();

  const form = useForm<AddQuoteInput>({
    defaultValues: { ...DEFAULT_VALUES, jobId: jobId ?? '' },
    resolver: zodResolver(addQuoteSchema),
  });

  const jobsQuery = useQuery({
    queryKey: jobsQueryKey(businessId, {}),
    queryFn: () => listJobs(businessId),
    enabled: !jobId,
  });

  const jobs = jobsQuery.data ?? [];

  function handleSubmit(data: AddQuoteInput) {
    onSave({
      jobId: data.jobId,
      line_items: toLineItems(data.lineItems),
      notes: data.notes ?? '',
      total_amount: data.manualTotal
        ? Number(data.totalAmount)
        : lineItemsTotal(data.lineItems),
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="font-semibold text-base">{t.addQuoteTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
            data-testid="modal-close"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={form.handleSubmit(handleSubmit)} className="overflow-y-auto flex-1">
          <div className="px-6 py-5 flex flex-col gap-4">
            {jobId ? (
              <div className="flex flex-col gap-1.5">
                <Label>{t.quoteJob}</Label>
                <p className="text-sm">{jobTitle}</p>
              </div>
            ) : (
              <Controller
                control={form.control}
                name="jobId"
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="quote-job">{t.quoteJob}</Label>
                    <select
                      {...field}
                      id="quote-job"
                      data-testid="quote-job-select"
                      aria-invalid={fieldState.invalid}
                      className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="" disabled>
                        {t.quoteJobPlaceholder}
                      </option>
                      {jobs.map((job) => (
                        <option key={job.id} value={job.id}>
                          {job.title}
                        </option>
                      ))}
                    </select>
                    {!jobsQuery.isPending && jobs.length === 0 && (
                      <p className="text-xs text-muted-foreground">{t.quoteNoJobs}</p>
                    )}
                    {fieldState.error && (
                      <p className="text-xs text-destructive">{fieldState.error.message}</p>
                    )}
                  </div>
                )}
              />
            )}

            <LineItemsField
              control={form.control as unknown as Control<QuoteFieldsValues>}
            />

            <Controller
              control={form.control}
              name="notes"
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="quote-notes">{t.quoteNotes}</Label>
                  <textarea
                    {...field}
                    id="quote-notes"
                    rows={3}
                    placeholder={t.quoteNotesPlaceholder}
                    data-testid="quote-notes-input"
                    className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  />
                  {fieldState.error && (
                    <p className="text-xs text-destructive">{fieldState.error.message}</p>
                  )}
                </div>
              )}
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-6 py-4 border-t shrink-0">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              {t.cancel}
            </Button>
            <Button type="submit" size="sm" disabled={isSaving} data-testid="save-quote">
              {t.saveQuote}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
